<script setup lang="ts">
import { stickyNoteCreated } from "@/analytics/events/sticky";
import { trackEvent } from "@/analytics/track";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import type { StickyType } from "@/model/stickyType";

import { useDragToCreateSticky } from "../../useDragToCreateSticky";

const props = defineProps<{ current: boolean; type: StickyType; selectable: boolean }>();
const emit = defineEmits<{ create: [type: StickyType]; select: [type: StickyType] }>();

const { handlePointerDown: pointerDown } = useDragToCreateSticky({
  mode: "create",
  getCardProps: (dragStart) => ({ pos: dragStart, type: props.type }),
  trackingLabel: "board-actions-menu-drag",
  onDragStart: () => {
    const { functionality, origin } = props.type;
    trackEvent(stickyNoteCreated(functionality, origin, "board-actions-menu-drag"));
  },
});
</script>

<template>
  <button
    :aria-label="type.name"
    role="menuitem"
    :tabindex="selectable ? 0 : -1"
    class="add-item-card action"
    :class="{ current }"
    @click.left.stop="emit('create', type)"
    @click.right.prevent="emit('select', type)"
    @pointerdown="pointerDown"
  >
    <TypeDot size="large" :color="type.color" />
  </button>
</template>

<style lang="scss">
.add-item-card {
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 0;
  height: 36px;
  width: 36px;
  margin: auto;
  padding: 6px 4px;

  .type-dot {
    transition: 0.15s ease-in-out transform;
    height: 32px;
    width: 32px;
  }

  &:hover {
    .type-dot {
      transform: scale(1.125);
    }
  }

  &:focus-visible {
    outline: revert;
  }
}
</style>
